/**
 * DepreciationPanel —— 折舊試算 ＋ 過帳（P2-6；`decisions/009`）
 * 由 `Assets.tsx` 抽出（頁面檔行數預算 ≤200）：
 * 揀期間 → **試算**（唔改帳）→ 一撳**過帳**（一個期間一張 JV，逐項資產一對 line）。
 * ⚠️ 一個期間只可以過一次；已過帳／冇需要折舊 → server 會擋（409／422）。
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, fmtMoney, type DepreciationPreview } from '../lib/api';

export default function DepreciationPanel({ entityId, onPosted }: {
  entityId: string;
  onPosted: (voucherNo: string, totalCents: number, rows: number) => void;
}) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState<DepreciationPreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    setErr(null); setBusy(true);
    try { setPreview(await api.depreciationPreview(entityId, period)); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); setPreview(null); }
    finally { setBusy(false); }
  };

  const doPost = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.postDepreciation(entityId, period);
      onPosted(r.voucherNo, r.totalCents, r.rows.length);
      setPreview(null);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fields">
        <label className="field">
          <span>{t('assets.period')}</span>
          <input type="month" value={period} onChange={(e) => { setPeriod(e.target.value); setPreview(null); }} />
        </label>
      </div>
      <button onClick={() => void doPreview()} disabled={busy}>{t('assets.preview')}</button>
      {err && <p className="err">{err}</p>}
      {preview && (
        <>
          {preview.alreadyPosted && (
            <p className="err">{t('assets.already_posted', { no: preview.alreadyPosted.voucherNo })}</p>
          )}
          {preview.rows.length === 0 ? <p className="note">{t('assets.nothing')}</p> : (
            <table className="ledger">
              <thead>
                <tr>
                  <th>{t('assets.asset_no')}</th><th>{t('assets.name')}</th>
                  <th className="num">{t('assets.cost')}</th><th className="num">{t('assets.accumulated')}</th>
                  <th className="num">{t('assets.book_value')}</th><th className="num">{t('assets.this_period')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.assetId}>
                    <td>{r.assetNo}</td><td>{r.name}</td>
                    <td className="num">{fmtMoney(r.costCents)}</td>
                    <td className="num">{fmtMoney(r.accumulatedCents)}</td>
                    <td className="num">{fmtMoney(r.remainingCents)}</td>
                    <td className="num"><b>{fmtMoney(r.amountCents)}</b></td>
                  </tr>
                ))}
                <tr>
                  <th colSpan={5}>{t('assets.total')}（{preview.totals.count}）</th>
                  <td className="num"><b>{fmtMoney(preview.totals.amountCents)}</b></td>
                </tr>
              </tbody>
            </table>
          )}
          <button className="cta" onClick={() => void doPost()}
            disabled={busy || preview.rows.length === 0 || Boolean(preview.alreadyPosted)}>
            {t('assets.post')}
          </button>
          <p className="note">{t('assets.post_note')}</p>
        </>
      )}
    </>
  );
}
